import {ApplicationRef, inject, Injectable} from '@angular/core';
import {SwUpdate} from '@angular/service-worker';
import {I18nService} from '@shared/services/i18n.service';
import {catchError, concat, first, interval, of} from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class PwaCheckForUpdateService {
  private appRef = inject(ApplicationRef);
  private swUpdate = inject(SwUpdate);
  private i18n = inject(I18nService);

  private isNotify = false;

  public listenForUpdate(): void {
    if (this.swUpdate?.isEnabled) {
      const appIsStable$ = this.appRef.isStable.pipe(
        first((isStable: boolean) => {
          // console.log(['appIsStable$', isStable]);
          return isStable === true;
        }),
        // timeout(35000), // 30 секунд
        catchError(() => {
          // console.warn('Перевірка стабільності програми закінчилася');
          return of(false); // Безпечний fallback
        })
      );
      // version refresh check interval
      const every10Minutes$ = interval(30000);
      const onceAppIsStable$ = concat(appIsStable$, every10Minutes$);

      onceAppIsStable$.subscribe(() => {
        // console.log(['onceAppIsStable$', e]);
        void this.swUpdate.checkForUpdate();
      });

      // Слухач для сповіщення про нову версію
      this.swUpdate.versionUpdates.subscribe((event) => {
        // console.log(['versionUpdates', event]);
        if (event.type === 'VERSION_DETECTED') {
          console.log(`New version detected: ${event.version.hash}`); //**
        } else if (event.type === 'VERSION_READY') {
          // console.log(`Нова версія готова до активації: ${event.latestVersion.hash}`);
          // this.activateUpdate();
          this.promptUserForUpdate();
        } else if (event.type === 'VERSION_INSTALLATION_FAILED') {
          // console.error(`Не вдалося встановити нову версію: ${event.version.hash}`);
          console.error(`Failed to install new version:${event.version.hash}`);
        }
      });

      // Слухач для критичних помилок
      this.swUpdate.unrecoverable.subscribe((_) => {
        // console.error(['Сталася невиправна помилка:', event]);
        // alert('Додаток непридатний для використання. Будь ласка, оновіть сторінку.');
        alert('The application is unusable. Please refresh the page.');
        this.cachesDelete();
      });
    }
  }

  private activateUpdate(): void {
    this.swUpdate
      .activateUpdate()
      .then(() => {
        // console.log('Оновлення активовано, сторінка перезавантажується.');
        document.location.reload();
      })
      .catch((error) => {
        console.error('Error during activation:', error);
      });
  }

  private promptUserForUpdate(): void {
    if (this.isNotify) {
      return;
    }
    this.createModalUpdateApp();
  }

  public cachesDelete(): void {
    if (!('caches' in window)) {
      return;
    }

    window.caches.keys().then((data: string[]) => {
      if (data && Array.isArray(data)) {
        data.forEach((keyCache) => {
          if (keyCache && keyCache.startsWith(`ngsw:/`)) {
            void window.caches.delete(keyCache);
          }
        });
      }
    });
  }

  createModalUpdateApp(): void {
    const notificationBlock = document.createElement('div');
    notificationBlock.className = [
      'd-flex',
      'flex-column',
      'flex-lg-row ',
      'justify-content-center',
      'align-items-lg-center',
      'position-fixed',
      'top-0',
      'vw-100',
      'p-2',
      'shadow-sm',
    ].join(' ');

    const applyButton = document.createElement('button');
    const cancelButton = document.createElement('button');
    const text = document.createElement('div');
    text.className = 'flex-grow-1 flex-lg-grow-0 text-center py-2 py-lg-0 text-lg-start';

    applyButton.className = 'btn btn-phoenix-success mx-2';
    cancelButton.className = 'btn btn-phoenix-info ';

    const notificationBlockHeight = '64px';
    notificationBlock.style.cssText = `
      background: var(--phoenix-navbar-vertical-bg-color);
      z-index: 2147483647;
      height: max-content;
      min-height: ${notificationBlockHeight};
    `;

    applyButton.innerText = this.i18n.translate('btn.yes');
    cancelButton.innerText = this.i18n.translate('btn.later');

    text.textContent = this.i18n.translate('msg.app_update');
    notificationBlock.appendChild(text);

    const el = document.createElement('div');
    el.className = 'btn-group w-100 w-lg-auto';
    el.appendChild(applyButton);
    el.appendChild(cancelButton);
    notificationBlock.appendChild(el);

    document.body.appendChild(notificationBlock);

    applyButton.addEventListener('click', () => this.activateUpdate());
    cancelButton.addEventListener('click', () => {
      notificationBlock.remove();
      this.isNotify = false;
    });

    const newspaperSpinning = [{transform: `translateY(-${notificationBlockHeight})`}, {transform: 'translateY(0px)'}];

    const newspaperTiming = {
      duration: 500,
      iterations: 1,
    };

    notificationBlock.animate(newspaperSpinning, newspaperTiming);

    this.isNotify = true;
  }
}
